/* eslint-disable */
import React, { useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Links } from './links';
import { useMiniappSdk } from 'app/shared/contexts/miniapp-sdk';
import { HEADER_BUTTON_MODE } from '@hqo/hqo-miniapp-client-sdk';

export type TitleType = 'normal' | 'bold';
export type LinkItem = {
  linkText: string;
  linkRef: string;
  isPhoneNumber?: boolean;
  shouldOpenInNewTab?: boolean;
}

export interface IconBlockInfo {
  title: string;
  icon: string;
  subtitle?: string;
  linkText?: string;
  linkRef?: string;
  titleType?: TitleType;
  shouldOpenInNewTab?: boolean;
}

export interface IconBlockProps {
  title: string;
  titleType?: TitleType;
  icon: string;
  iconType?: string;
  subtitle?: string | JSX.Element;
  linkText?: string;
  linkRef?: string;
  className?: string;
  dataCy?: string;
  dataTestId?: string;
  isPhoneNumber?: boolean;
  useHoverStyles?: boolean;
  onClick?: VoidFunction;
  shouldOpenInNewTab?: boolean;
  isDisabled?: boolean;
  onActionClick?: VoidFunction;
  action?: boolean;
  actionText?: string;
  links?: Array<LinkItem>;
}

interface ContainerProps {
  isDisabled?: boolean;
  useHoverStyles?: boolean;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  children: React.ReactNode;
  className?: string;
  'data-cy'?: string;
  'data-testid'?: string;
}

interface TitleProps {
  useHoverStyles?: boolean;
  titleType?: string;
  children: React.ReactNode;
  className?: string;
}

interface LinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  isPhoneNumber?: boolean;
  children: React.ReactNode;
}

interface ActionButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
}

const Container = ({ isDisabled, useHoverStyles, onClick, children, className = '', ...props }: ContainerProps) => {
  const baseClasses = 'flex flex-row w-full';
  const hoverClasses = useHoverStyles
    ? 'bg-gray-100 rounded h-[92px] px-3 sm:px-[14px] py-2 sm:py-[11px] pb-1 sm:pb-[7px] hover:bg-white hover:shadow-lg hover:cursor-pointer transition-all duration-200'
    : '';
  const disabledClasses = isDisabled ? 'pointer-events-none' : '';

  return (
    <div
      className={`${baseClasses} ${hoverClasses} ${disabledClasses} ${className}`}
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      onClick={onClick}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      {children}
    </div>
  );
};

const TextContainer = ({ children, className = '', ...props }: { children: React.ReactNode; className?: string; [key: string]: any }) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <div className={`flex flex-col ${className}`} {...props}>
    {children}
  </div>
);

const Icon = ({ icon, className = '', ...props }: { icon: IconProp; className?: string; [key: string]: any }) => (
  <FontAwesomeIcon
    icon={icon}
    className={`text-lg mr-2 text-gray-700 ${className}`}
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...props}
  />
);

const Title = ({ useHoverStyles, titleType, children, className = '', ...props }: TitleProps) => {
  // eslint-disable-next-line no-nested-ternary
  const fontWeight = useHoverStyles ? 'font-bold' : titleType === 'bold' ? 'font-bold' : 'font-normal';

  return (
    <div
      className={`text-lg leading-6 pb-1 text-gray-700 ${fontWeight} ${className}`}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      {children}
    </div>
  );
};

const Subtitle = ({ children, className = '', ...props }: { children: React.ReactNode; className?: string; [key: string]: any }) => (
  <div
    className={`text-lg leading-5 pb-2 font-normal text-gray-600 ${className}`}
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...props}
  >
    {children}
  </div>
);

const Link = ({ isPhoneNumber, children, className = '', ...props }: LinkProps) => {
  const colorClasses = isPhoneNumber
    ? 'text-gray-700 no-underline'
    : 'text-utility-blue-light-700 underline';

  return (
    <a
      className={`font-medium text-lg leading-5 ${colorClasses} ${className}`}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      {children}
    </a>
  );
};

// const LinksContainer = ({ children, className = '', ...props }: { children: React.ReactNode; className?: string; [key: string]: any }) => (
//   <div
//     className={`flex justify-start items-center flex-wrap gap-y-[5px] gap-x-8 ${className}`}
//     // eslint-disable-next-line react/jsx-props-no-spreading
//     {...props}
//   >
//     {children}
//   </div>
// );

const ActionButton = ({ children, className = '', ...props }: ActionButtonProps) => (
  // eslint-disable-next-line react/button-has-type
  <button
    className={`bg-transparent border-none p-0 text-pink-500 text-sm font-normal cursor-pointer underline hover:no-underline flex justify-start ${className}`}
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...props}
  >
    {children}
  </button>
);

export const IconBlock: React.FC<IconBlockProps> = ({
  title,
  titleType = 'normal',
  icon,
  iconType,
  subtitle,
  linkText,
  linkRef,
  className = 'icon-block',
  dataCy = 'icon-block',
  dataTestId = 'icon-block',
  isPhoneNumber,
  useHoverStyles,
  onClick,
  shouldOpenInNewTab,
  isDisabled,
  actionText,
  action,
  onActionClick,
  links,
}: IconBlockProps) => {
  const sdkClient = useMiniappSdk();

  const handleOnClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      if (onClick) onClick();
    },
    [onClick],
  );

  const handleOnActionClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (onActionClick) onActionClick();
  }, [onActionClick]);

  const handleLinkRedirect = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    sdkClient?.header?.showHeader();
    sdkClient?.header?.setHeader('', HEADER_BUTTON_MODE.HISTORY);
  }, [sdkClient]);

  const iconProp = useMemo(() => (iconType ? [iconType, icon] : ['fas', icon]), [iconType, icon]) as IconProp;

  return (
    <Container
      isDisabled={isDisabled}
      data-cy={dataCy}
      data-testid={dataTestId}
      className={className}
      useHoverStyles={useHoverStyles}
      onClick={handleOnClick}
    >
      <Icon className="icon-block-icon" icon={iconProp} />
      <TextContainer className="icon-block-text-container" data-cy={`${dataCy}-text-container`}>
        {title && (
          <Title className="icon-block-title" useHoverStyles={useHoverStyles} titleType={titleType}>
            {title}
          </Title>
        )}
        {subtitle && <Subtitle className="icon-block-subtitle">{subtitle}</Subtitle>}
        {linkText && linkRef && (
          <Link
            className="icon-block-link"
            href={isPhoneNumber ? `tel:+${linkRef}` : linkRef}
            isPhoneNumber={isPhoneNumber}
            onClick={handleLinkRedirect}
            target={shouldOpenInNewTab ? '_blank' : '_self'}
          >
            {linkText}
          </Link>
        )}
        {Boolean(links?.length) && <Links links={links} handleLinkRedirect={handleLinkRedirect} />}
        {action && actionText && onActionClick && (
          <ActionButton
            type="button"
            data-testid="icon-block-action"
            onClick={handleOnActionClick}
          >
            {actionText}

          </ActionButton>
        )}
      </TextContainer>
    </Container>
  );
};
